/**
 * Firebase Data Wrapper for Self-Hosted Excalidraw
 * =================================================
 *
 * This module wraps excalidraw-app/data/firebase.ts and overrides
 * `loadFilesFromFirebase` to fetch files from our backend instead of
 * directly from Firebase Storage.
 *
 * The original function constructs hardcoded firebasestorage.googleapis.com
 * URLs, bypassing our firebase-backend.ts adapter. This wrapper fixes that.
 *
 * See CUSTOMIZATIONS.md for maintenance instructions.
 */

import { MIME_TYPES } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";

import type { FileId } from "@excalidraw/element/types";
import type {
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

// Re-export everything from the original firebase.ts
// These imports will go through our firebase-backend.ts adapter via Vite aliases
export {
  loadFirebaseStorage,
  isSavedToFirebase,
  saveFilesToFirebase,
  saveToFirebase,
  loadFromFirebase,
} from "../excalidraw-app/data/firebase";

// Get backend URL from environment
const BACKEND_URL = import.meta.env.VITE_APP_FIREBASE_BACKEND_URL || '';

/**
 * Load files from our backend instead of Firebase Storage.
 *
 * The original function constructs URLs like:
 *   https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{prefix}%2F{id}?alt=media
 *
 * We instead fetch from:
 *   {BACKEND_URL}/api/files/{prefix}/{id}
 */
export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  if (!BACKEND_URL) {
    console.warn('[Firebase Data Wrapper] VITE_APP_FIREBASE_BACKEND_URL not set, cannot load files');
    filesIds.forEach(id => erroredFiles.set(id, true));
    return { loadedFiles, erroredFiles };
  }

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        // Normalize prefix: remove leading slash and 'files/' prefix
        // (matching the normalization done in firebase-backend.ts ref())
        let normalizedPrefix = prefix.replace(/^\//, '');
        normalizedPrefix = normalizedPrefix.replace(/^files\//, '');
        const url = `${BACKEND_URL}/api/files/${normalizedPrefix}/${id}`;

        console.log('[Firebase Data Wrapper] Loading file:', url);

        const response = await fetch(url);
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          console.warn('[Firebase Data Wrapper] Failed to load file:', url, response.status);
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error('[Firebase Data Wrapper] Error loading file:', error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
